import React from 'react';
import {
  Controller,
  type FieldError,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { FormInput } from './FormInput';
import { RegionSelector } from './RegionSelector';
import { formFieldInStack } from './SubsectionForm';
import { colors, spacing, fontSize, lineHeight, fontWeight } from '../../theme';
import { PermissionAwareField } from '../PermissionAwareField';

import type {
  RequestFormRhfFieldContext,
  RequestFormRhfFieldDef,
} from './rhfFieldTypes';

function rhfTextNode<TRhf extends FieldValues>(
  def: Extract<RequestFormRhfFieldDef<TRhf>, { kind: 'rhfText' }>,
  ctx: RequestFormRhfFieldContext<TRhf>,
): React.ReactNode {
  const disabledFromDef =
    def.isDisabledOverride?.(ctx.formData, ctx.isDisabled) ?? ctx.isDisabled;
  const labelIcon = def.getLabelIcon?.(ctx.formData) ?? null;

  const fieldControl = (
    <Controller<TRhf>
      control={ctx.control}
      name={def.name as Path<TRhf>}
      render={({ field: { onChange, onBlur, value, ref } }) => (
        <FormInput
          ref={ref}
          label={def.label}
          value={
            typeof value === 'string'
              ? value
              : value == null
              ? ''
              : String(value)
          }
          onBlur={onBlur}
          onChangeText={v => {
            onChange(v);
            ctx.updateFormData(def.formSyncKey, v);
          }}
          placeholder={def.placeholder}
          required={def.required}
          disabled={disabledFromDef}
          readOnlyMode={ctx.isDisabled}
          labelIcon={labelIcon == null ? undefined : labelIcon}
          error={ctx.mergeFieldError(
            ctx.rhfErrors[def.name] as FieldError | undefined,
            ctx.validationErrors,
            def.formSyncKey,
          )}
          errorElementId={def.errorElementId}
          containerStyle={formFieldInStack}
          testID={def.testID}
        />
      )}
    />
  );

  if (def.permissionFieldId) {
    return (
      <PermissionAwareField
        fieldId={def.permissionFieldId}
        requestStage={ctx.requestStage}
        requestId={ctx.requestId}
      >
        {fieldControl}
      </PermissionAwareField>
    );
  }
  return fieldControl;
}

function rhfRegionNode<TRhf extends FieldValues>(
  def: Extract<RequestFormRhfFieldDef<TRhf>, { kind: 'rhfRegion' }>,
  ctx: RequestFormRhfFieldContext<TRhf>,
): React.ReactNode {
  const hideEmbeddedLabel = def.hideEmbeddedLabel === true;
  const triggerTextLines = def.triggerTextLines === 2 ? 2 : 1;

  const regionControl = (
    <Controller<TRhf>
      control={ctx.control}
      name={def.name as Path<TRhf>}
      render={({ field: { onChange, value } }) => (
        <RegionSelector
          label={hideEmbeddedLabel ? '' : def.label}
          value={
            typeof value === 'string'
              ? value
              : value == null
              ? ''
              : String(value)
          }
          onSelect={v => {
            onChange(v);
            ctx.updateFormData(def.formSyncKey, v);
          }}
          placeholder={def.placeholder}
          required={def.required}
          disabled={ctx.isDisabled}
          readOnlyMode={ctx.isDisabled}
          error={ctx.mergeFieldError(
            ctx.rhfErrors[def.name] as FieldError | undefined,
            ctx.validationErrors,
            def.formSyncKey,
          )}
          optionSubtitles={def.optionSubtitles}
          allowClear={def.allowClear}
          triggerTextLines={triggerTextLines}
          containerStyle={formFieldInStack}
          testID={def.testID}
        />
      )}
    />
  );

  const body = hideEmbeddedLabel ? (
    <View style={rhfRegionStyles.fieldStack}>
      <View style={rhfRegionStyles.labelRow}>
        <Text style={rhfRegionStyles.formFieldLabel}>
          {def.label}
          {def.required ? (
            <Text style={rhfRegionStyles.requiredMark}> *</Text>
          ) : null}
        </Text>
      </View>
      {regionControl}
    </View>
  ) : (
    regionControl
  );

  if (def.permissionFieldId) {
    return (
      <PermissionAwareField
        fieldId={def.permissionFieldId}
        requestStage={ctx.requestStage}
        requestId={ctx.requestId}
      >
        {body}
      </PermissionAwareField>
    );
  }
  return body;
}

const rhfRegionStyles = StyleSheet.create({
  fieldStack: {
    width: '100%' as const,
    alignSelf: 'stretch' as const,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.fieldVerticalGap,
    ...Platform.select({
      web: { overflow: 'visible' as const },
      default: {},
    }),
  },
  formFieldLabel: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  requiredMark: { color: colors.error },
});

/**
 * Maps declarative `RequestFormRhfFieldDef` values to the shared Project Details form control stack
 * (Controller, optional `PermissionAwareField`, `formFieldInStack` spacing, sync to `updateFormData`).
 * Pair rows use `PairedRhfFieldRow`; the body is still `FormFieldStack` inside `StepForm`.
 */
export function renderRequestFormRhfField<TRhf extends FieldValues>(
  def: RequestFormRhfFieldDef<TRhf>,
  ctx: RequestFormRhfFieldContext<TRhf>,
): React.ReactNode {
  switch (def.kind) {
    case 'rhfText':
      return rhfTextNode(def, ctx);
    case 'rhfRegion':
      return rhfRegionNode(def, ctx);
    case 'custom':
      return def.node;
    default: {
      const _exhaustive: never = def;
      return _exhaustive;
    }
  }
}

/**
 * Two equal columns in a `FormFieldStack` row; parent supplies styles from
 * `getFieldRowStyleResponsive` / `webPairedFieldColumn` so each form step keeps one source of
 * layout tokens while fields come from the factory.
 */
export function PairedRhfFieldRow({
  style,
  leftStyle,
  rightStyle,
  left,
  right,
  testID,
}: {
  style: StyleProp<ViewStyle>;
  leftStyle: StyleProp<ViewStyle>;
  rightStyle: StyleProp<ViewStyle>;
  left: React.ReactNode;
  right: React.ReactNode;
  testID?: string;
}): React.ReactElement {
  return (
    <View style={style} testID={testID}>
      <View style={leftStyle}>{left}</View>
      <View style={rightStyle}>{right}</View>
    </View>
  );
}

PairedRhfFieldRow.displayName = 'PairedRhfFieldRow';
